import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  EyeOff,
  Send,
  Vote,
  CheckCircle,
  Crown,
  MessageCircle,
  LogOut,
  AlertTriangle,
  Sparkles,
  Users,
  Timer,
  Trophy,
  Target,
  X,
  Zap,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useGameStore } from '@/store';
import { useWebSocket } from '@/hooks/useWebSocket';
import { logger } from '@/utils/logger';
import { soundManager } from '@/utils/soundManager';

// Word categories for display
const CATEGORY_NAMES: Record<string, string> = {
  places: '📍 أماكن',
  food: '🍕 طعام',
  animals: '🦁 حيوانات',
  jobs: '👨‍⚕️ مهن',
  sports: '⚽ رياضة',
};

// Voting timer duration in seconds
const VOTING_DURATION = 60;

export function GamePage() {
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const {
    player,
    room,
    players,
    isHost,
    currentRound,
    isSpy,
    secretWord,
    setIsSpy,
    setSecretWord,
    setCurrentRound,
    messages,
    addMessage,
    reset,
  } = useGameStore();

  // Local state
  const [showWord, setShowWord] = useState(false);
  const [selectedVote, setSelectedVote] = useState<string | null>(null);
  const [votesUsed, setVotesUsed] = useState(0); // Track number of votes used
  const [maxVotesPerPlayer, setMaxVotesPerPlayer] = useState(1); // 1 for 1-spy, 2 for 2-spy
  const [myVotes, setMyVotes] = useState<string[]>([]); // Track my own votes
  const [chatInput, setChatInput] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [wordCategory, setWordCategory] = useState<string>('');
  const [totalVotes, setTotalVotes] = useState(0);
  const [totalVotesNeeded, setTotalVotesNeeded] = useState(0);
  const [results, setResults] = useState<{
    spyIds: string[];
    spyNames: string[];
    spyCaught: boolean;
    secretWord: string;
    mostVotedNames: string[];
    voteDetails: { voterName: string; targetName: string; isSkip?: boolean }[];
    // New fields for 2-spy support
    totalSpies?: number;
    spiesCaughtCount?: number;
    allSpiesCaught?: boolean;
    caughtSpyIds?: string[];
    caughtSpyNames?: string[];
    escapedSpyIds?: string[];
    escapedSpyNames?: string[];
    playersWin?: boolean;
    spiesWin?: boolean;
    isTie?: boolean; // New field for tie detection
  } | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  
  // Word reveal popup state
  const [showWordReveal, setShowWordReveal] = useState(false);
  
  // Voting timer state
  const [votingStarted, setVotingStarted] = useState(false);
  const [votingTimeLeft, setVotingTimeLeft] = useState(VOTING_DURATION);
  const votingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const votingSectionRef = useRef<HTMLDivElement>(null);
  
  // Ref to track if results are shown (sync update for timer logic)
  const resultsShownRef = useRef(false);
  
  // Live votes tracking - who voted for whom
  const [liveVotes, setLiveVotes] = useState<{ voterId: string; voterName: string; targetId: string; isSkip: boolean }[]>([]);
  
  // Sound settings
  const [soundEnabled, setSoundEnabled] = useState(soundManager.isEnabled());
  
  // Notifications for player join/leave
  const [notifications, setNotifications] = useState<{ id: string; message: string; type: 'join' | 'leave' }[]>([]);
  
  // Spectator mode - for players who joined mid-game
  const [isSpectator, setIsSpectator] = useState(false);
  // List of spectator IDs (players who can't be voted on)
  const [spectatorIds, setSpectatorIds] = useState<string[]>([]);
  
  const processedMessages = useRef(new Set<string>());
  const chatEndRef = useRef<HTMLDivElement>(null);

  const { isConnected, sendMessage, on } = useWebSocket(
    code!,
    player?.id!,
    player?.name!
  );

  // Toggle sound
  const toggleSound = () => {
    const newState = soundManager.toggle();
    setSoundEnabled(newState);
  };

  // Add notification with auto-remove after 5 seconds
  const addNotification = useCallback((message: string, type: 'join' | 'leave') => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, message, type }]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  // Force end voting when timer expires - any player can send, server validates
  const forceEndVoting = useCallback(() => {
    // Check ref for immediate sync state (not stale closure)
    if (resultsShownRef.current) {
      logger.info('GamePage', '⏰ Force end skipped - results already shown (ref check)');
      return;
    }
    
    if (votingStarted && !showResults) {
      logger.info('GamePage', '⏰ Timer expired, forcing vote end', {
        votingStarted,
        showResults,
        votingTimeLeft,
      });
      sendMessage({ type: 'vote:force-end', payload: {} });
    } else {
      logger.info('GamePage', '⏰ Force end skipped', {
        votingStarted,
        showResults,
        votingTimeLeft,
      });
    }
  }, [votingStarted, showResults, votingTimeLeft, sendMessage]);

  // Start voting timer
  const startVotingTimer = useCallback(() => {
    if (votingTimerRef.current) {
      clearInterval(votingTimerRef.current);
    }
    
    setVotingStarted(true);
    setVotingTimeLeft(VOTING_DURATION);
    
    votingTimerRef.current = setInterval(() => {
      setVotingTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(votingTimerRef.current!);
          votingTimerRef.current = null;
          return 0;
        }
        // 🔊 Play warning sound at 10 seconds
        if (prev === 11) {
          soundManager.playTimerWarning();
        }
        return prev - 1;
      });
    }, 1000);
    
    // Scroll to voting section
    setTimeout(() => {
      votingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
  }, []);

  // ============== WebSocket Message Handlers ==============
  // Use callback-based approach for reliable message handling

  // Handle round:start
  useEffect(() => {
    const unsubscribe = on('round:start', (message) => {
      logger.info('GamePage', '🎮 ROUND:START received', { payload: message.payload });
      
      const roundData = message.payload as {
        roundNumber: number;
        isSpy: boolean;
        secretWord: string | null;
        category?: string;
        players?: any[];
        isLateJoin?: boolean;
        isReconnect?: boolean;
        resultsAlreadyShown?: boolean;
        settings?: { spyCount: number; category: string };
        spectatorIds?: string[];
      };
      
      // Update spectator list
      setSpectatorIds(roundData.spectatorIds || []);
      
      // Check if this is a late join (spectator mode)
      if (roundData.isLateJoin) {
        setIsSpectator(true);
        setCurrentRound(roundData.roundNumber);
        setIsSpy(false);
        setSecretWord(null);
        setWordCategory(roundData.category || '');
        if (roundData.players) {
          useGameStore.getState().setPlayers(roundData.players);
        }
        addNotification('أنت متفرج حالياً - ستشارك في الجولة القادمة', 'join');
        return;
      }
      
      // If reconnecting and results already shown
      if (roundData.isReconnect && roundData.resultsAlreadyShown) {
        logger.info('GamePage', '🔄 Reconnected with results already shown');
        setCurrentRound(roundData.roundNumber);
        setIsSpy(roundData.isSpy);
        setSecretWord(roundData.secretWord);
        setWordCategory(roundData.category || '');
        if (roundData.players) {
          useGameStore.getState().setPlayers(roundData.players);
        }
        return;
      }
      
      // Regular round start
      setIsSpectator(false);
      setCurrentRound(roundData.roundNumber);
      setIsSpy(roundData.isSpy);
      setSecretWord(roundData.secretWord);
      setWordCategory(roundData.category || '');
      setVotesUsed(0);
      setMyVotes([]);
      setSelectedVote(null);
      setShowResults(false);
      resultsShownRef.current = false;
      setTotalVotes(0);
      setTotalVotesNeeded(0);
      setVotingStarted(false);
      setVotingTimeLeft(VOTING_DURATION);
      setShowWord(false);
      setLiveVotes([]);
      
      if (roundData.settings?.spyCount === 2) {
        setMaxVotesPerPlayer(2);
      } else {
        setMaxVotesPerPlayer(1);
      }
      
      if (roundData.players) {
        useGameStore.getState().setPlayers(roundData.players);
      }
      
      if (!roundData.isReconnect) {
        soundManager.playGameStart();
        setTimeout(() => {
          if (roundData.isSpy) {
            soundManager.playSpyReveal();
          } else {
            soundManager.playPlayerReveal();
          }
        }, 500);
        setShowWordReveal(true);
      }
    });
    
    return unsubscribe;
  }, [on, addNotification, setCurrentRound, setIsSpy, setSecretWord]);

  // Handle vote:results - THE CRITICAL ONE!
  useEffect(() => {
    const unsubscribe = on('vote:results', (message) => {
      logger.info('GamePage', '🎯🎯🎯 VOTE RESULTS RECEIVED 🎯🎯🎯', { payload: message.payload });
      
      // IMMEDIATELY mark results as shown
      resultsShownRef.current = true;
      
      // IMMEDIATELY stop voting timer
      if (votingTimerRef.current) {
        clearInterval(votingTimerRef.current);
        votingTimerRef.current = null;
        logger.info('GamePage', '⏱️ Voting timer STOPPED');
      }
      
      const voteResults = message.payload as {
        spyIds: string[];
        spyNames: string[];
        spyCaught: boolean;
        secretWord: string;
        mostVotedNames: string[];
        voteDetails: { voterName: string; targetName: string; isSkip?: boolean }[];
        players: any[];
        totalSpies?: number;
        spiesCaughtCount?: number;
        allSpiesCaught?: boolean;
        caughtSpyIds?: string[];
        caughtSpyNames?: string[];
        escapedSpyIds?: string[];
        escapedSpyNames?: string[];
        playersWin?: boolean;
        spiesWin?: boolean;
        isTie?: boolean;
      };
      
      setVotingStarted(false);
      setVotingTimeLeft(VOTING_DURATION);
      
      // Play sounds
      if (!voteResults.isTie) {
        const playersWon = voteResults.playersWin ?? voteResults.spyCaught;
        const didIWin = playersWon 
          ? !voteResults.spyIds.includes(player?.id || '')
          : voteResults.spyIds.includes(player?.id || '');
        
        setTimeout(() => {
          if (didIWin) {
            soundManager.playWin();
          } else {
            soundManager.playLose();
          }
        }, 300);
      } else {
        soundManager.playError();
      }
      
      logger.info('GamePage', '📊 Setting results and showing popup', { 
        isTie: voteResults.isTie,
        spiesWin: voteResults.spiesWin,
        playersWin: voteResults.playersWin
      });
      
      setResults({
        spyIds: voteResults.spyIds,
        spyNames: voteResults.spyNames,
        spyCaught: voteResults.spyCaught,
        secretWord: voteResults.secretWord,
        mostVotedNames: voteResults.mostVotedNames,
        voteDetails: voteResults.voteDetails,
        totalSpies: voteResults.totalSpies,
        spiesCaughtCount: voteResults.spiesCaughtCount,
        allSpiesCaught: voteResults.allSpiesCaught,
        caughtSpyIds: voteResults.caughtSpyIds,
        caughtSpyNames: voteResults.caughtSpyNames,
        escapedSpyIds: voteResults.escapedSpyIds,
        escapedSpyNames: voteResults.escapedSpyNames,
        playersWin: voteResults.playersWin,
        spiesWin: voteResults.spiesWin,
        isTie: voteResults.isTie,
      });
      setShowResults(true);
      setShowWordReveal(false);
      setLiveVotes([]);
      
      if (voteResults.players) {
        useGameStore.getState().setPlayers(voteResults.players);
      }
    });
    
    return unsubscribe;
  }, [on, player?.id]);

  // Handle vote:received
  useEffect(() => {
    const unsubscribe = on('vote:received', (message) => {
      const voteInfo = message.payload as { 
        totalVotes: number; 
        totalPlayers: number;
        totalVotesNeeded: number;
        maxVotesPerPlayer: number;
        playerVotesUsed: number;
        playerId?: string;
        liveVotes?: { voterId: string; voterName: string; targetId: string; isSkip: boolean }[];
      };
      
      setTotalVotes(voteInfo.totalVotes);
      setTotalVotesNeeded(voteInfo.totalVotesNeeded || voteInfo.totalPlayers);
      
      if (voteInfo.maxVotesPerPlayer) {
        setMaxVotesPerPlayer(voteInfo.maxVotesPerPlayer);
      }
      
      if (voteInfo.playerId === player?.id && voteInfo.playerVotesUsed !== undefined) {
        setVotesUsed(voteInfo.playerVotesUsed);
      }
      
      if (voteInfo.liveVotes) {
        setLiveVotes(voteInfo.liveVotes);
      }
      
      if (voteInfo.playerId && voteInfo.playerId !== player?.id) {
        soundManager.playVoteReceived();
      }
      
      // Start voting timer on first vote
      if (voteInfo.totalVotes === 1 && !votingStarted) {
        soundManager.playVotingStart();
        startVotingTimer();
      }
    });
    
    return unsubscribe;
  }, [on, player?.id, votingStarted, startVotingTimer]);

  // Handle spectators:update - when a new spectator joins
  useEffect(() => {
    const unsubscribe = on('spectators:update', (message) => {
      const data = message.payload as {
        spectatorIds: string[];
        players?: any[];
      };
      logger.info('GamePage', '👀 Spectators updated', { spectatorIds: data.spectatorIds });
      setSpectatorIds(data.spectatorIds);
      if (data.players) {
        useGameStore.getState().setPlayers(data.players);
      }
    });
    
    return unsubscribe;
  }, [on]);

  // Handle chat:message
  useEffect(() => {
    const unsubscribe = on('chat:message', (message) => {
      const chatData = message.payload as {
        playerId: string;
        playerName: string;
        message: string;
      };
      const msgId = `${chatData.playerId}-${chatData.message}-${message.timestamp}`;
      if (!processedMessages.current.has(msgId)) {
        processedMessages.current.add(msgId);
        addMessage({
          id: msgId,
          ...chatData,
          timestamp: message.timestamp || new Date().toISOString(),
        });
        
        if (chatData.playerId !== player?.id) {
          soundManager.playMessageReceived();
        }
      }
    });
    
    return unsubscribe;
  }, [on, player?.id, addMessage]);

  // Handle room:player-joined
  useEffect(() => {
    const unsubscribe = on('room:player-joined', (message) => {
      const joinData = message.payload as { 
        playerId: string; 
        playerName: string; 
        players: any[];
        spectatorIds?: string[];
        isSpectator?: boolean;
      };
      if (joinData.players) {
        useGameStore.getState().setPlayers(joinData.players);
      }
      // Update spectator list if provided
      if (joinData.spectatorIds) {
        setSpectatorIds(joinData.spectatorIds);
        logger.info('GamePage', '👀 Spectators updated from player-joined', { spectatorIds: joinData.spectatorIds });
      }
      if (joinData.playerId !== player?.id && joinData.playerName) {
        const suffix = joinData.isSpectator ? ' (متفرج)' : '';
        addNotification(`${joinData.playerName} انضم للغرفة${suffix}`, 'join');
        soundManager.playSuccess();
      }
    });
    
    return unsubscribe;
  }, [on, player?.id, addNotification]);

  // Handle room:player-left
  useEffect(() => {
    const unsubscribe = on('room:player-left', (message) => {
      const leaveData = message.payload as { 
        playerId: string; 
        playerName?: string; 
        players: any[];
        spectatorIds?: string[];
      };
      if (leaveData.players) {
        useGameStore.getState().setPlayers(leaveData.players);
      }
      // Update spectator list if provided
      if (leaveData.spectatorIds) {
        setSpectatorIds(leaveData.spectatorIds);
      }
      if (leaveData.playerName && leaveData.playerId !== player?.id) {
        addNotification(`${leaveData.playerName} غادر الغرفة`, 'leave');
      }
    });
    
    return unsubscribe;
  }, [on, player?.id, addNotification]);

  // Handle room:closed
  useEffect(() => {
    const unsubscribe = on('room:closed', () => {
      soundManager.playError();
      alert('تم إغلاق الغرفة من قبل المضيف');
      reset();
      navigate('/');
    });
    
    return unsubscribe;
  }, [on, reset, navigate]);

  // Handle game:end
  useEffect(() => {
    const unsubscribe = on('game:end', () => {
      navigate(`/results/${code}`);
    });
    
    return unsubscribe;
  }, [on, code, navigate]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (votingTimerRef.current) {
        clearInterval(votingTimerRef.current);
      }
    };
  }, []);

  // Force end voting when timer reaches 0
  useEffect(() => {
    if (votingTimeLeft === 0 && votingStarted && !showResults && !resultsShownRef.current) {
      logger.info('GamePage', 'Voting timer expired, forcing end');
      forceEndVoting();
    }
  }, [votingTimeLeft, votingStarted, showResults, forceEndVoting]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Redirect if no player info
  useEffect(() => {
    if (!player || !room) {
      navigate(`/join/${code}`);
    }
  }, [player, room, code, navigate]);

  // Submit vote
  const handleVote = () => {
    if (!selectedVote || votesUsed >= maxVotesPerPlayer || isSpectator) {
      logger.info('GamePage', '🚫 Vote blocked', { 
        selectedVote, 
        votesUsed, 
        maxVotesPerPlayer, 
        isSpectator 
      });
      return;
    }
    logger.info('GamePage', '✅ Submitting vote', { targetId: selectedVote });
    sendMessage({
      type: 'vote:submit',
      payload: { targetId: selectedVote, isSkip: false },
    });
    setMyVotes(prev => [...prev, selectedVote]);
    setVotesUsed(prev => prev + 1);
    setSelectedVote(null); // Clear selection to allow another vote if double voting
  };

  // Skip vote
  const handleSkipVote = () => {
    if (votesUsed >= maxVotesPerPlayer || isSpectator) return;
    sendMessage({
      type: 'vote:submit',
      payload: { targetId: '', isSkip: true },
    });
    setMyVotes(prev => [...prev, 'SKIP']);
    setVotesUsed(prev => prev + 1);
    setSelectedVote(null);
  };

  // Check if I've voted for a specific player already
  const hasVotedFor = (targetId: string) => myVotes.includes(targetId);
  
  // Check if I've used all my votes
  const hasVotedAll = votesUsed >= maxVotesPerPlayer;

  // Get voters for a specific target (for live vote display)
  const getVotersForTarget = (targetId: string) => {
    return liveVotes.filter(v => v.targetId === targetId && !v.isSkip);
  };

  // Send chat message
  const handleSendChat = () => {
    if (!chatInput.trim() || isSpectator) return;
    sendMessage({
      type: 'chat:message',
      payload: { message: chatInput.trim() },
    });
    soundManager.playMessageSent();
    setChatInput('');
  };

  // Handle key press in chat
  const handleChatKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  };

  // Start next round (host only)
  const handleNextRound = () => {
    if (!isHost) return;
    sendMessage({ type: 'round:start', payload: {} });
  };

  // Exit game
  const handleExit = () => {
    if (isHost) {
      sendMessage({ type: 'room:close', payload: {} });
    } else {
      sendMessage({ type: 'room:leave', payload: {} });
    }
    reset();
    navigate('/');
  };

  // Calculate rounds won - now score is simply the number of wins
  const getWinsCount = (playerScore: number) => playerScore;

  // Filter out disconnected players and sort by score
  const connectedPlayers = players.filter(p => p.isConnected);
  const sortedPlayers = [...connectedPlayers].sort((a, b) => b.score - a.score);
  
  // Players eligible for voting (excluding spectators and disconnected)
  const votablePlayers = sortedPlayers.filter(p => !spectatorIds.includes(p.id));

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen p-4 pb-20 relative overflow-x-hidden">
      {/* ==================== NOTIFICATIONS ==================== */}
      <div className="fixed top-4 left-4 right-4 z-[100] flex flex-col items-center gap-2 pointer-events-none">
        <AnimatePresence>
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.9 }}
              transition={{ type: 'spring', damping: 20 }}
              className={`px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium pointer-events-auto ${
                notification.type === 'join'
                  ? 'bg-emerald-500/90 text-white'
                  : 'bg-orange-500/90 text-white'
              }`}
            >
              <span className="text-lg">
                {notification.type === 'join' ? '👋' : '👋'}
              </span>
              <span>{notification.message}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* ==================== WORD REVEAL POPUP ==================== */}
      <AnimatePresence>
        {showWordReveal && currentRound > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md overflow-y-auto"
          >
            <div className="min-h-full flex items-center justify-center p-4 py-8">
            <motion.div
              initial={{ scale: 0.5, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.5, y: 50 }}
              transition={{ type: 'spring', damping: 20 }}
              className={`relative w-full max-w-md overflow-hidden rounded-3xl shadow-2xl ${
                isSpy 
                  ? 'bg-gradient-to-br from-red-900 via-red-800 to-red-900 border-4 border-red-500' 
                  : 'bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 border-4 border-emerald-500'
              }`}
            >
              {/* Animated background particles */}
              <div className="absolute inset-0 overflow-hidden">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-2 h-2 rounded-full ${isSpy ? 'bg-red-400/30' : 'bg-emerald-400/30'}`}
                    initial={{ 
                      x: Math.random() * 400, 
                      y: Math.random() * 600,
                      scale: Math.random() * 0.5 + 0.5
                    }}
                    animate={{ 
                      y: [null, Math.random() * -100 - 50],
                      opacity: [0.7, 0]
                    }}
                    transition={{ 
                      duration: Math.random() * 2 + 2, 
                      repeat: Infinity,
                      ease: 'linear'
                    }}
                  />
                ))}
              </div>
              
              <div className="relative p-8 text-center">
                {/* Close button */}
                <button
                  onClick={() => setShowWordReveal(false)}
                  className="absolute top-4 left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                
                {/* Round badge */}
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="inline-block mb-4"
                >
                  <span className="bg-white/20 text-white px-4 py-1 rounded-full text-sm font-bold">
                    🎮 الجولة {currentRound}
                  </span>
                </motion.div>
                
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', damping: 10, delay: 0.3 }}
                  className={`w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center shadow-2xl ${
                    isSpy 
                      ? 'bg-gradient-to-br from-red-500 to-red-700 shadow-red-500/50' 
                      : 'bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-500/50'
                  }`}
                >
                  <span className="text-7xl">{isSpy ? '🕵️' : '🎯'}</span>
                </motion.div>
                
                {/* Title */}
                <motion.h2
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-3xl font-bold text-white mb-2"
                >
                  {isSpy ? '🔴 أنت الجاسوس!' : '🟢 أنت لاعب عادي'}
                </motion.h2>
                
                {/* Category */}
                {wordCategory && (
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mb-4"
                  >
                    <span className={`inline-block px-4 py-2 rounded-full text-lg font-bold ${
                      isSpy ? 'bg-red-500/30 text-red-200' : 'bg-emerald-500/30 text-emerald-200'
                    }`}>
                      {CATEGORY_NAMES[wordCategory] || wordCategory}
                    </span>
                  </motion.div>
                )}
                
                {/* Word or spy message */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mb-6"
                >
                  {isSpy ? (
                    <div className="space-y-4">
                      <p className="text-red-200 text-lg">
                        الكلمة السرية مخفية عنك!
                      </p>
                      <div className="bg-red-800/50 rounded-xl p-4 text-right">
                        <p className="text-yellow-300 font-bold mb-2">⚠️ مهمتك:</p>
                        <ul className="text-red-100 text-sm space-y-1">
                          <li>• اكتشف الكلمة من خلال المحادثة</li>
                          <li>• تصرف بشكل طبيعي</li>
                          <li>• لا تكشف نفسك</li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-emerald-200 text-lg mb-2">الكلمة السرية:</p>
                      <div className="relative inline-block">
                        <motion.div
                          animate={{ 
                            boxShadow: ['0 0 20px rgba(16, 185, 129, 0.5)', '0 0 40px rgba(16, 185, 129, 0.8)', '0 0 20px rgba(16, 185, 129, 0.5)']
                          }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="bg-white/20 backdrop-blur-sm rounded-2xl px-8 py-4"
                        >
                          <span className="text-4xl font-bold text-white">
                            {showWord ? secretWord : '••••••'}
                          </span>
                        </motion.div>
                        
                        <button
                          onClick={() => setShowWord(!showWord)}
                          className="absolute -left-14 top-1/2 -translate-y-1/2 p-3 rounded-xl bg-white/20 hover:bg-white/30 transition-colors"
                        >
                          {showWord ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                        </button>
                      </div>
                      
                      <div className="bg-emerald-800/50 rounded-xl p-4 text-right mt-4">
                        <p className="text-yellow-300 font-bold mb-2">💡 مهمتك:</p>
                        <ul className="text-emerald-100 text-sm space-y-1">
                          <li>• لا تذكر الكلمة مباشرة</li>
                          <li>• اكتشف من هو الجاسوس</li>
                          <li>• صوّت لكشفه</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </motion.div>
                
                {/* Start button */}
                <motion.button
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowWordReveal(false)}
                  className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg ${
                    isSpy 
                      ? 'bg-white text-red-700 hover:bg-red-50' 
                      : 'bg-white text-emerald-700 hover:bg-emerald-50'
                  }`}
                >
                  <Zap className="w-5 h-5 inline ml-2" />
                  فهمت! ابدأ اللعب
                </motion.button>
              </div>
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== RESULTS POPUP ==================== */}
      <AnimatePresence>
        {showResults && results && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md overflow-y-auto"
          >
            <div className="min-h-full flex items-start justify-center p-4 py-8">
            {(() => {
              // Check for tie first
              const isTieResult = results.isTie;
              
              // Determine if current player won or lost (only if not tie)
              // For 2-spy games use playersWin, otherwise use legacy spyCaught
              const playersWon = results.playersWin ?? results.spyCaught;
              const playerWon = isTieResult ? false : (isSpy ? !playersWon : playersWon);
              const totalSpies = results.totalSpies ?? 1;
              const spiesCaught = results.spiesCaughtCount ?? (results.spyCaught ? 1 : 0);
              const is2SpyGame = totalSpies === 2;
              
              return (
                <motion.div
                  initial={{ scale: 0.5, y: 100 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.5, y: 100 }}
                  transition={{ type: 'spring', damping: 20 }}
                  className={`relative w-full max-w-lg overflow-hidden rounded-3xl shadow-2xl ${
                    isTieResult
                      ? (isSpy 
                          ? 'bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 border-4 border-emerald-400'
                          : 'bg-gradient-to-br from-red-900 via-red-800 to-orange-900 border-4 border-red-400')
                      : playerWon 
                        ? 'bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 border-4 border-emerald-400' 
                        : 'bg-gradient-to-br from-red-900 via-red-800 to-orange-900 border-4 border-red-400'
                  }`}
                >
                  {/* Confetti for winner (not for tie) */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {!isTieResult && playerWon && [...Array(30)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-3 h-3 rounded-full"
                        style={{ 
                          background: ['#10B981', '#34D399', '#6EE7B7', '#A7F3D0', '#FFD700'][i % 5],
                          left: `${Math.random() * 100}%`
                        }}
                        initial={{ y: -20, opacity: 1, scale: Math.random() * 0.5 + 0.5 }}
                        animate={{ 
                          y: 600,
                          opacity: 0,
                          rotate: Math.random() * 360
                        }}
                        transition={{ 
                          duration: Math.random() * 2 + 1.5,
                          repeat: Infinity,
                          ease: 'linear',
                          delay: Math.random() * 2
                        }}
                      />
                    ))}
                  </div>
                  
                  <div className="relative p-8 text-center">
                    {/* Personal Result Banner */}
                    <motion.div
                      initial={{ y: -50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className={`mb-4 py-3 px-6 rounded-full inline-block ${
                        isTieResult
                          ? (isSpy 
                              ? 'bg-emerald-400/30 border-2 border-emerald-400 text-emerald-300'
                              : 'bg-red-400/30 border-2 border-red-400 text-red-300')
                          : playerWon 
                            ? 'bg-emerald-400/30 border-2 border-emerald-400 text-emerald-300' 
                            : 'bg-red-400/30 border-2 border-red-400 text-red-300'
                      }`}
                    >
                      <span className="text-xl font-bold">
                        {isTieResult ? (isSpy ? '🎉 أنت فزت!' : '😢 أنت خسرت!') : playerWon ? '🎉 أنت فزت!' : '😢 أنت خسرت!'}
                      </span>
                    </motion.div>
                    
                    {/* Result icon */}
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', damping: 10 }}
                      className={`w-36 h-36 mx-auto mb-6 rounded-full flex items-center justify-center shadow-2xl ${
                        isTieResult
                          ? (isSpy 
                              ? 'bg-gradient-to-br from-emerald-400 to-teal-600 shadow-emerald-500/50'
                              : 'bg-gradient-to-br from-red-400 to-orange-600 shadow-red-500/50')
                          : playerWon 
                            ? 'bg-gradient-to-br from-emerald-400 to-teal-600 shadow-emerald-500/50' 
                            : 'bg-gradient-to-br from-red-400 to-orange-600 shadow-red-500/50'
                      }`}
                    >
                      <motion.span 
                        className="text-8xl"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                      >
                        {isTieResult ? (isSpy ? '🏆' : '💔') : playerWon ? '🏆' : '💔'}
                      </motion.span>
                    </motion.div>
                    
                    {/* Result title - shows game outcome */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h2 className="text-3xl font-bold text-white mb-2">
                        {isTieResult 
                          ? '😈 الجواسيس فازوا بالتعادل!'
                          : is2SpyGame 
                            ? (playersWon 
                                ? '🎉 تم كشف كل الجواسيس!' 
                                : spiesCaught === 1 
                                  ? '😈 جاسوس واحد نجا!' 
                                  : '😈 الجواسيس نجوا!')
                            : (playersWon ? '🕵️ تم كشف الجاسوس!' : '😈 الجاسوس نجا!')
                        }
                      </h2>
                      <p className="text-lg text-white/80 mb-4">
                        {isTieResult 
                          ? 'تعادل في التصويت = الجواسيس لم يُكتشفوا!'
                          : isSpy 
                            ? (playersWon 
                                ? 'للأسف تم كشفك! اللاعبون فازوا' 
                                : 'أحسنت! نجحت في خداعهم')
                            : (playersWon 
                                ? 'أحسنتم! كشفتم الجاسوس بنجاح' 
                                : is2SpyGame && spiesCaught === 1
                                  ? 'للأسف! كشفتم جاسوس واحد فقط - كان يجب كشفهم جميعاً'
                                  : 'للأسف! الجاسوس خدعكم')
                        }
                      </p>
                      {/* Show caught vs escaped for 2-spy games */}
                      {is2SpyGame && (
                        <div className="flex gap-4 justify-center mb-2">
                          <div className={`px-3 py-1 rounded-full text-sm ${
                            spiesCaught > 0 ? 'bg-emerald-500/30 text-emerald-300' : 'bg-white/10 text-white/50'
                          }`}>
                            ✅ مكشوف: {spiesCaught}
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm ${
                            totalSpies - spiesCaught > 0 ? 'bg-red-500/30 text-red-300' : 'bg-white/10 text-white/50'
                          }`}>
                            ❌ ناجي: {totalSpies - spiesCaught}
                          </div>
                        </div>
                      )}
                    </motion.div>
                    
                    {/* Your role reminder */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.35 }}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${
                        isSpy ? 'bg-red-500/30 text-red-300' : 'bg-blue-500/30 text-blue-300'
                      }`}
                    >
                      <span>{isSpy ? '🕵️' : '👤'}</span>
                      <span className="text-sm font-medium">
                        كنت {isSpy ? 'الجاسوس' : 'لاعب عادي'}
                      </span>
                    </motion.div>
                    
                    {/* Spy reveal */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className={`rounded-2xl p-4 mb-4 ${
                        playersWon ? 'bg-emerald-800/50' : 'bg-red-800/50'
                      }`}
                    >
                      {is2SpyGame ? (
                        // 2-spy game: show caught and escaped separately
                        <div className="space-y-3">
                          {/* Caught spies */}
                          {results.caughtSpyNames && results.caughtSpyNames.length > 0 && (
                            <div className="flex items-center justify-center gap-3 bg-emerald-500/20 rounded-xl p-3">
                              <span className="text-2xl">✅</span>
                              <div>
                                <p className="text-emerald-300 text-sm">تم كشفهم</p>
                                <p className="text-xl font-bold text-white">
                                  {results.caughtSpyNames.join(' و ')}
                                </p>
                              </div>
                            </div>
                          )}
                          {/* Escaped spies */}
                          {results.escapedSpyNames && results.escapedSpyNames.length > 0 && (
                            <div className="flex items-center justify-center gap-3 bg-red-500/20 rounded-xl p-3">
                              <span className="text-2xl">❌</span>
                              <div>
                                <p className="text-red-300 text-sm">نجوا من الكشف</p>
                                <p className="text-xl font-bold text-white">
                                  {results.escapedSpyNames.join(' و ')}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        // 1-spy game: original display
                        <div className="flex items-center justify-center gap-3 mb-2">
                          <span className="text-3xl">🕵️</span>
                          <div>
                            <p className="text-white/60 text-sm">
                              {results.spyNames.length > 1 ? 'الجواسيس' : 'الجاسوس كان'}
                            </p>
                            <p className="text-2xl font-bold text-white">
                              {results.spyNames.join(' و ')}
                            </p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                    
                    {/* Secret word reveal */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="bg-white/10 rounded-2xl p-4 mb-6"
                    >
                      <p className="text-white/60 text-sm mb-1">الكلمة السرية</p>
                      <p className="text-3xl font-bold text-game-gold">
                        ✨ {results.secretWord} ✨
                      </p>
                    </motion.div>
                    
                    {/* Vote details */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6 }}
                      className="bg-white/10 rounded-2xl p-4 mb-6 text-right"
                    >
                      <h4 className="font-bold mb-3 text-white flex items-center gap-2 justify-end">
                        <span>📊 تفاصيل التصويت</span>
                      </h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {results.voteDetails && results.voteDetails.length > 0 ? (
                          results.voteDetails.map((vote, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm bg-white/5 rounded-lg p-2">
                              <span className="text-white/60">{vote.voterName}</span>
                              <span className="flex items-center gap-1">
                                {vote.isSkip ? (
                                  <span className="text-yellow-400">⏭️ تخطي</span>
                                ) : (
                                  <>
                                    <span className="text-white/40">صوّت لـ</span>
                                    <span className={`font-bold ${
                                      results.spyNames.includes(vote.targetName) ? 'text-game-gold' : 'text-white/80'
                                    }`}>
                                      {vote.targetName}
                                      {results.spyNames.includes(vote.targetName) && ' 🕵️'}
                                    </span>
                                  </>
                                )}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-white/40 text-center">لا توجد أصوات</p>
                        )}
                      </div>
                    </motion.div>
                    
                    {/* Points message */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.7 }}
                      className={`rounded-2xl p-4 mb-6 ${
                        isTieResult
                          ? 'bg-gradient-to-r from-yellow-500/30 to-amber-500/30 border border-yellow-400/50'
                          : playerWon 
                            ? 'bg-gradient-to-r from-emerald-500/30 to-teal-500/30 border border-emerald-400/50' 
                            : 'bg-gradient-to-r from-red-500/30 to-orange-500/30 border border-red-400/50'
                      }`}
                    >
                      {isTieResult ? (
                        <div className="flex items-center justify-center gap-3">
                          <div className="text-4xl">😈</div>
                          <div>
                            <p className="text-red-300 font-bold">
                              {isSpy ? '+1 نقطة لك! 🎊' : 'لا نقاط هذه الجولة 😔'}
                            </p>
                            <p className="text-sm text-white/60">
                              {isSpy ? 'التعادل يعني أنك لم تُكتشف!' : 'التعادل = فوز الجواسيس'}
                            </p>
                          </div>
                        </div>
                      ) : playerWon ? (
                        <div className="flex items-center justify-center gap-3">
                          <Trophy className="w-8 h-8 text-game-gold" />
                          <div>
                            <p className="text-emerald-300 font-bold">+1 نقطة لك! 🎊</p>
                            <p className="text-sm text-white/60">
                              {isSpy ? 'نجحت في خداع الجميع' : 'ساعدت في كشف الجاسوس'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-3">
                          <Crown className="w-8 h-8 text-red-400" />
                          <div>
                            <p className="text-red-300 font-bold">لا نقاط هذه الجولة 😔</p>
                            <p className="text-sm text-white/60">حظ أوفر في الجولة القادمة!</p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                    
                    {/* Action buttons */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.8 }}
                      className="flex gap-3"
                    >
                      <button
                        onClick={() => setShowResults(false)}
                        className="flex-1 py-3 rounded-xl font-bold bg-white/20 hover:bg-white/30 transition-colors"
                      >
                        إغلاق
                      </button>
                      {isHost && (
                        isTieResult ? (
                          <button
                            onClick={() => {
                              setShowResults(false);
                              handleNextRound();
                            }}
                            className="flex-1 py-3 rounded-xl font-bold bg-white text-gray-900 hover:bg-white/90 transition-colors"
                          >
                            🎮 الجولة التالية
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setShowResults(false);
                              handleNextRound();
                            }}
                            className="flex-1 py-3 rounded-xl font-bold bg-white text-gray-900 hover:bg-white/90 transition-colors"
                          >
                            🎮 الجولة التالية
                          </button>
                        )
                      )}
                    </motion.div>
                  </div>
                </motion.div>
              );
            })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== EXIT CONFIRMATION ==================== */}
      <AnimatePresence>
        {showExitConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowExitConfirm(false)}
          >
            <div className="min-h-full flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-game-card p-6 rounded-2xl shadow-2xl max-w-sm text-center border border-white/10"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-500" />
                </div>
                
                <h3 className="text-xl font-bold mb-2">
                  {isHost ? 'إغلاق الغرفة؟' : 'مغادرة اللعبة؟'}
                </h3>
                
                <p className="text-white/60 mb-6">
                  {isHost 
                    ? 'سيتم إغلاق الغرفة وطرد جميع اللاعبين'
                    : 'سيتم خروجك من اللعبة الحالية'
                  }
                </p>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowExitConfirm(false)}
                    className="flex-1 btn-outline"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleExit}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-xl transition-colors"
                  >
                    {isHost ? 'إغلاق' : 'مغادرة'}
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== SPECTATOR BANNER ==================== */}
      <AnimatePresence>
        {isSpectator && currentRound > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-16 left-4 right-4 z-50 flex justify-center pointer-events-none"
          >
            <div className="bg-amber-500/90 text-white px-6 py-3 rounded-2xl shadow-lg flex items-center gap-3 pointer-events-auto">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Eye className="w-5 h-5" />
              </div>
              <div className="text-right">
                <p className="font-bold">أنت متفرج 👀</p>
                <p className="text-sm text-white/80">ستشارك في الجولة القادمة - شاهد فقط!</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== MAIN CONTENT ==================== */}
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowExitConfirm(true)}
              className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
            <div>
              <p className="text-white/60 text-sm">الجولة {currentRound || 1}</p>
              <h1 className="text-2xl font-bold">🕵️ لعبة الجاسوس</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSound}
              className={`p-2 rounded-lg transition-colors ${
                soundEnabled 
                  ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400' 
                  : 'bg-white/10 hover:bg-white/20 text-white/40'
              }`}
              title={soundEnabled ? 'كتم الصوت' : 'تشغيل الصوت'}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <div className="badge badge-accent">{code}</div>
          </div>
        </motion.div>

        {/* Main Game Area */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Game Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Quick Word Card */}
            {!showResults && currentRound > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`card relative overflow-hidden ${
                  isSpy 
                    ? 'border-2 border-red-500/50 bg-gradient-to-r from-red-900/30 to-red-800/20' 
                    : 'border-2 border-emerald-500/50 bg-gradient-to-r from-emerald-900/30 to-emerald-800/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isSpy ? 'bg-red-500/20' : 'bg-emerald-500/20'
                    }`}>
                      <span className="text-2xl">{isSpy ? '🕵️' : '🎯'}</span>
                    </div>
                    <div>
                      <p className={`text-sm ${isSpy ? 'text-red-400' : 'text-emerald-400'}`}>
                        {isSpy ? 'أنت الجاسوس' : 'الكلمة السرية'}
                      </p>
                      {isSpy ? (
                        <p className="text-lg font-bold text-white/60">???</p>
                      ) : (
                        <p className={`text-2xl font-bold ${showWord ? 'text-game-gold' : 'blur-sm text-white/30'}`}>
                          {secretWord}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {wordCategory && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isSpy ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'
                      }`}>
                        {CATEGORY_NAMES[wordCategory] || wordCategory}
                      </span>
                    )}
                    {!isSpy && (
                      <button
                        onClick={() => setShowWord(!showWord)}
                        className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                      >
                        {showWord ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    )}
                    <button
                      onClick={() => setShowWordReveal(true)}
                      className={`p-2 rounded-lg transition-colors ${
                        isSpy ? 'bg-red-500/20 hover:bg-red-500/30' : 'bg-emerald-500/20 hover:bg-emerald-500/30'
                      }`}
                    >
                      <Sparkles className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Waiting for round to start */}
            {!showResults && currentRound === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="card text-center py-12"
              >
                <motion.div 
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-20 h-20 mx-auto mb-4 bg-primary-500/20 rounded-full flex items-center justify-center"
                >
                  <span className="text-4xl">⏳</span>
                </motion.div>
                <h3 className="text-xl font-bold mb-2">في انتظار بدء الجولة</h3>
                <p className="text-white/60 mb-4">
                  {isHost ? 'اضغط على الزر أدناه لبدء اللعب' : 'انتظر المضيف لبدء الجولة'}
                </p>
                {isHost && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleNextRound}
                    className="btn-primary text-lg px-8 py-3"
                  >
                    🎮 بدء الجولة الأولى
                  </motion.button>
                )}
              </motion.div>
            )}

            {/* ==================== VOTING SECTION ==================== */}
            {!showResults && currentRound > 0 && (
              <motion.div
                ref={votingSectionRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`card relative overflow-hidden ${
                  votingStarted ? 'border-2 border-game-accent/50' : ''
                }`}
              >
                {/* Voting Timer Alert */}
                {votingStarted && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className={`-mx-6 -mt-6 mb-4 p-4 ${
                      votingTimeLeft <= 10 
                        ? 'bg-red-500/30 border-b border-red-500/50' 
                        : 'bg-game-accent/20 border-b border-game-accent/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <motion.div
                          animate={{ scale: votingTimeLeft <= 10 ? [1, 1.2, 1] : 1 }}
                          transition={{ duration: 0.5, repeat: votingTimeLeft <= 10 ? Infinity : 0 }}
                        >
                          <Timer className={`w-6 h-6 ${votingTimeLeft <= 10 ? 'text-red-400' : 'text-game-accent'}`} />
                        </motion.div>
                        <span className="font-bold">وقت التصويت</span>
                      </div>
                      <div className={`text-2xl font-bold font-mono ${
                        votingTimeLeft <= 10 ? 'text-red-400' : 'text-game-accent'
                      }`}>
                        {formatTime(votingTimeLeft)}
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                      <motion.div
                        className={`h-2 rounded-full ${votingTimeLeft <= 10 ? 'bg-red-500' : 'bg-game-accent'}`}
                        initial={{ width: '100%' }}
                        animate={{ width: `${(votingTimeLeft / VOTING_DURATION) * 100}%` }}
                        transition={{ duration: 1, ease: 'linear' }}
                      />
                    </div>
                    
                    {votingTimeLeft <= 10 && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-red-300 text-sm mt-2 text-center"
                      >
                        ⚠️ سارع بالتصويت قبل انتهاء الوقت!
                      </motion.p>
                    )}
                  </motion.div>
                )}
                
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Vote className="w-5 h-5 text-game-accent" />
                  صوّت لمن تعتقد أنه الجاسوس
                  {!votingStarted && !isSpectator && (
                    <span className="text-xs text-white/40 font-normal">
                      (المؤقت يبدأ عند أول تصويت)
                    </span>
                  )}
                  {isSpectator && (
                    <span className="text-xs text-amber-400 font-normal">
                      (مشاهدة فقط)
                    </span>
                  )}
                </h3>

                {/* Spectator Notice */}
                {isSpectator && (
                  <div className="mb-4 bg-amber-500/20 border border-amber-500/50 rounded-xl p-3 text-center">
                    <p className="text-amber-300 text-sm">
                      👀 أنت متفرج - لا يمكنك التصويت في هذه الجولة
                    </p>
                  </div>
                )}

                {/* Voting Progress */}
                <div className="mb-4 bg-white/5 rounded-xl p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-white/60">التصويت</span>
                    <span className="text-sm font-bold text-game-gold">
                      {totalVotes} / {totalVotesNeeded || players.length * maxVotesPerPlayer}
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <motion.div 
                      className="bg-game-gold h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${(totalVotes / (totalVotesNeeded || players.length * maxVotesPerPlayer)) * 100}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  {/* Double voting indicator */}
                  {maxVotesPerPlayer === 2 && !isSpectator && (
                    <div className="mt-2 text-center">
                      <span className="text-xs text-game-accent">
                        🗳️ لديك صوتين - استخدمت {votesUsed} من {maxVotesPerPlayer}
                      </span>
                    </div>
                  )}
                </div>

                {/* Player voting buttons - excluding spectators */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {votablePlayers
                    .map((p) => {
                      const isSelf = p.id === player?.id;
                      const alreadyVotedForThis = hasVotedFor(p.id);
                      const canVote = !isSelf && !hasVotedAll && !alreadyVotedForThis;
                      
                      return (
                        <motion.button
                          key={p.id}
                          whileHover={{ scale: canVote ? 1.02 : 1 }}
                          whileTap={{ scale: canVote ? 0.98 : 1 }}
                          onClick={() => canVote && setSelectedVote(p.id)}
                          disabled={!canVote}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            isSelf
                              ? 'border-primary-500/50 bg-primary-500/10 cursor-default'
                              : alreadyVotedForThis
                                ? 'border-emerald-500/50 bg-emerald-500/20 cursor-default'
                              : selectedVote === p.id
                                ? 'border-game-accent bg-game-accent/20 shadow-lg shadow-game-accent/20'
                                : hasVotedAll 
                                  ? 'border-white/20 opacity-50 cursor-not-allowed'
                                  : 'border-white/20 hover:border-white/40'
                          }`}
                        >
                          <div className="flex items-center gap-2 relative">
                            <div className="relative">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                isSelf 
                                  ? 'bg-gradient-to-br from-primary-500 to-primary-700 ring-2 ring-primary-400' 
                                  : 'bg-gradient-to-br from-primary-400 to-primary-600'
                              }`}>
                                {p.name.charAt(0)}
                              </div>
                              {/* Live vote indicators - show who voted for this player */}
                              {getVotersForTarget(p.id).length > 0 && (
                                <div className="absolute -top-2 -right-2 flex -space-x-2">
                                  {getVotersForTarget(p.id).slice(0, 3).map((voter, idx) => (
                                    <motion.div
                                      key={voter.voterId}
                                      initial={{ scale: 0, y: -10 }}
                                      animate={{ scale: 1, y: 0 }}
                                      className="w-6 h-6 rounded-full bg-game-accent border-2 border-game-card flex items-center justify-center text-xs font-bold shadow-lg"
                                      style={{ zIndex: 10 - idx }}
                                      title={voter.voterName}
                                    >
                                      {voter.voterName.charAt(0)}
                                    </motion.div>
                                  ))}
                                  {getVotersForTarget(p.id).length > 3 && (
                                    <div className="w-6 h-6 rounded-full bg-white/20 border-2 border-game-card flex items-center justify-center text-xs">
                                      +{getVotersForTarget(p.id).length - 3}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right flex-1">
                              <span className="font-bold block truncate">
                                {p.name}
                                {isSelf && <span className="text-xs text-primary-400 mr-1">(أنت)</span>}
                              </span>
                              {p.role === 'host' && (
                                <span className="text-xs text-game-gold flex items-center gap-1">
                                  <Crown className="w-3 h-3" /> المضيف
                                </span>
                              )}
                              {/* Vote count badge */}
                              {getVotersForTarget(p.id).length > 0 && (
                                <span className={`text-xs ${isSelf ? 'text-red-400' : 'text-game-accent'}`}>
                                  🗳️ {getVotersForTarget(p.id).length} صوت {isSelf && '⚠️'}
                                </span>
                              )}
                            </div>
                            {selectedVote === p.id && !isSelf && (
                              <Target className="w-5 h-5 text-game-accent" />
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                </div>

                {/* Vote and Skip buttons */}
                {!isSpectator && (
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: hasVotedAll || !selectedVote ? 1 : 1.02 }}
                      whileTap={{ scale: hasVotedAll || !selectedVote ? 1 : 0.98 }}
                      onClick={handleVote}
                      disabled={!selectedVote || hasVotedAll}
                      className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                        hasVotedAll
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                          : selectedVote
                            ? 'bg-game-accent hover:bg-game-accent/80 text-white' 
                            : 'bg-white/10 text-white/40'
                      }`}
                    >
                      {hasVotedAll ? (
                        <span className="flex items-center justify-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          تم التصويت ({votesUsed}/{maxVotesPerPlayer}) ✓
                        </span>
                      ) : votesUsed > 0 ? (
                        `🗳️ صوّت مرة أخرى (${votesUsed}/${maxVotesPerPlayer})`
                      ) : (
                        '🗳️ تأكيد التصويت'
                    )}
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: hasVotedAll ? 1 : 1.02 }}
                    whileTap={{ scale: hasVotedAll ? 1 : 0.98 }}
                    onClick={handleSkipVote}
                    disabled={hasVotedAll}
                    className={`px-6 py-3 rounded-xl font-bold transition-all ${
                      hasVotedAll
                        ? 'bg-white/5 text-white/30'
                        : 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30'
                    }`}
                  >
                    {hasVotedAll ? (
                      <span className="flex items-center justify-center gap-2">
                        ⏭️ تم
                      </span>
                    ) : (
                      '⏭️ تخطي'
                    )}
                  </motion.button>
                  </div>
                )}
                
                {/* Skip count info */}
                {liveVotes.filter(v => v.isSkip).length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-3 text-center text-sm text-yellow-400/80"
                  >
                    ⏭️ {liveVotes.filter(v => v.isSkip).length} لاعب تخطى التصويت
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* ==================== CHAT SECTION ==================== */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card"
            >
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary-400" />
                المحادثة
              </h3>

              <div className="h-64 overflow-y-auto mb-4 space-y-3 scrollbar-thin scrollbar-thumb-white/10">
                {messages.length === 0 ? (
                  <div className="text-center text-white/40 py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>لا توجد رسائل بعد</p>
                    <p className="text-sm">ابدأ المحادثة!</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, x: msg.playerId === player?.id ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`flex ${msg.playerId === player?.id ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-2xl ${
                          msg.playerId === player?.id
                            ? 'bg-primary-500/20 rounded-tr-sm'
                            : 'bg-white/10 rounded-tl-sm'
                        }`}
                      >
                        <p className="text-xs text-white/50 mb-1">
                          {msg.playerName}
                          {sortedPlayers.find(p => p.id === msg.playerId)?.role === 'host' && (
                            <Crown className="w-3 h-3 inline ml-1 text-game-gold" />
                          )}
                        </p>
                        <p className="text-sm">{msg.message}</p>
                      </div>
                    </motion.div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              {isSpectator ? (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-3 text-center">
                  <p className="text-amber-300 text-sm">
                    👀 أنت متفرج - لا يمكنك إرسال رسائل حتى الجولة القادمة
                  </p>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={handleChatKeyPress}
                    placeholder="اكتب رسالة..."
                    className="input flex-1"
                    maxLength={200}
                  />
                  <button
                    onClick={handleSendChat}
                    disabled={!chatInput.trim()}
                    className="btn-primary px-4"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              )}
            </motion.div>
          </div>

          {/* ==================== SIDEBAR ==================== */}
          <div className="space-y-6">
            {/* Players List */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="card"
            >
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary-400" />
                اللاعبون ({sortedPlayers.length})
                {spectatorIds.length > 0 && (
                  <span className="text-xs text-amber-400">({spectatorIds.length} متفرج)</span>
                )}
              </h3>

              <div className="space-y-2">
                {sortedPlayers.map((p, index) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                      spectatorIds.includes(p.id)
                        ? 'bg-amber-500/10 border border-amber-500/30 opacity-70'
                        : p.id === player?.id
                          ? 'bg-primary-500/20 border border-primary-500/30'
                          : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        spectatorIds.includes(p.id)
                          ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                          : index === 0 
                            ? 'bg-gradient-to-br from-game-gold to-yellow-600' 
                            : 'bg-gradient-to-br from-game-accent to-pink-500'
                      }`}>
                        {spectatorIds.includes(p.id) ? '👀' : p.name.charAt(0)}
                      </div>
                      {index === 0 && !spectatorIds.includes(p.id) && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-game-gold rounded-full flex items-center justify-center text-xs shadow-lg">
                          👑
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate flex items-center gap-1">
                        {p.name}
                        {p.id === player?.id && <span className="text-xs text-white/40">(أنت)</span>}
                        {spectatorIds.includes(p.id) && <span className="text-xs text-amber-400">(متفرج)</span>}
                      </p>
                      <div className="flex items-center gap-1 text-sm">
                        {spectatorIds.includes(p.id) ? (
                          <span className="text-amber-400">سيشارك في الجولة القادمة</span>
                        ) : (
                          <>
                            <Trophy className="w-3 h-3 text-game-gold" />
                            <span className="text-game-gold">{getWinsCount(p.score)} جولة</span>
                          </>
                        )}
                      </div>
                    </div>
                    {p.role === 'host' && (
                      <Crown className="w-4 h-4 text-game-gold flex-shrink-0" />
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Host Actions */}
            {isHost && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="card"
              >
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-game-gold" />
                  إجراءات المضيف
                </h3>
                {currentRound > 0 && !showResults && (
                  <button
                    onClick={handleNextRound}
                    className="btn-outline w-full mb-2"
                  >
                    🔄 جولة جديدة
                  </button>
                )}
                <button
                  onClick={() => setShowExitConfirm(true)}
                  className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 font-bold py-2 px-4 rounded-xl transition-colors"
                >
                  ❌ إغلاق الغرفة
                </button>
              </motion.div>
            )}

            {/* Non-host exit */}
            {!isHost && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <button
                  onClick={() => setShowExitConfirm(true)}
                  className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 font-bold py-2 px-4 rounded-xl transition-colors"
                >
                  <LogOut className="w-4 h-4 inline ml-2" />
                  مغادرة اللعبة
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
